/**
 * Uploading a file into a template: sign → PUT straight to storage → confirm.
 *
 * The bytes never pass through our server. The server hands out a short-lived
 * signed URL (POST /files/sign, after checking the plan's limits), the browser
 * PUTs the file to it with progress, and POST /files/confirm checks what
 * landed (real size, first bytes) and marks the file ready. A refusal at any
 * step is an Error whose message is the server's reason, worded for the user.
 *
 * The XMLHttpRequest class is a parameter so tests can run the whole flow
 * against a fake; fetch has no upload progress.
 */
import * as Backend from '../Backend';
import { quotaProblem } from './fileLimits';

export class FileUploadError extends Error {
  constructor(message, step) {
    super(message);
    this.name = 'FileUploadError';
    this.step = step; // 'check' | 'sign' | 'put' | 'confirm' | 'cancelled'
  }
}

/** True for the error a cancelled upload rejects with: nothing to show the user. */
export const isCancelled = (e) => Boolean(e && e.step === 'cancelled');
const cancelled = () => new FileUploadError('Upload cancelled', 'cancelled');

/** The PUT, with progress. Resolves on 2xx; rejects with the status otherwise, or when `signal` aborts. */
function putFile(url, headers, file, onProgress, XHR, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XHR();
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.open('PUT', url, true);
    for (const [k, v] of Object.entries(headers || {})) xhr.setRequestHeader(k, v);
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (HTTP ${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed: the connection was lost'));
    xhr.onabort = () => reject(cancelled());
    xhr.send(file);
  });
}

/**
 * @param file      a File from an <input type="file">
 * @param opts.usage, opts.limits   when known, checked here first so a refusal is
 *                  instant; the server checks again regardless
 * @param opts.onProgress(pct)
 * @param opts.signal  an AbortSignal: aborting stops the upload at any step and
 *                  releases what the server holds for it (the pending row, or
 *                  the file itself once confirmed); rejects with isCancelled
 * @returns {{ fileId, name, size, mime }}
 */
export async function uploadFile(sessionToken, file, opts = {}) {
  const XHR = opts.XHR || window.XMLHttpRequest;
  const { signal } = opts;
  const name = file.name;
  const size = file.size;
  const release = (fileId) => Promise.resolve(Backend.deleteFile(sessionToken, fileId)).catch(() => undefined);

  if (opts.limits) {
    const problem = quotaProblem(opts.usage || { files: 0, bytes: 0 }, opts.limits, { name, size });
    if (problem) throw new FileUploadError(problem, 'check');
  }

  if (signal && signal.aborted) throw cancelled();
  const signed = await Backend.signFile(sessionToken, { name, size });
  if (!signed || signed.error || !signed.url) throw new FileUploadError((signed && signed.error) || 'Could not prepare the upload', 'sign');
  if (signal && signal.aborted) {
    release(signed.fileId);
    throw cancelled();
  }

  try {
    await putFile(signed.url, signed.headers, file, opts.onProgress, XHR, signal);
  } catch (e) {
    // the pending row would count toward the quota for a day: let it go now
    release(signed.fileId);
    if (isCancelled(e)) throw e;
    throw new FileUploadError(e.message || 'Upload failed', 'put');
  }

  const confirmed = await Backend.confirmFile(sessionToken, signed.fileId);
  if (!confirmed || confirmed.error || !confirmed.fileId) throw new FileUploadError((confirmed && confirmed.error) || 'Could not confirm the upload', 'confirm');
  // cancelled while confirming: the file is ready but no cell will hold it, so release it
  if (signal && signal.aborted) {
    release(confirmed.fileId);
    throw cancelled();
  }
  return { fileId: confirmed.fileId, name: confirmed.name, size: confirmed.size, mime: confirmed.mime };
}

/**
 * What the editors hand the file cell editor: every call bound to the
 * session token App holds, so no component needs it.
 */
export function makeFileService(getSessionToken, XHR) {
  return {
    upload: (file, opts = {}) => uploadFile(getSessionToken(), file, { ...opts, XHR }),
    remove: (fileId) => Backend.deleteFile(getSessionToken(), fileId),
    usage: () => Backend.filesUsage(getSessionToken()),
    downloadUrl: async (fileId) => {
      const r = await Backend.fileDownloadUrl(getSessionToken(), fileId);
      if (!r || r.error || !r.url) throw new Error((r && r.error) || 'Could not prepare the download');
      return r.url;
    },
  };
}
